// src/components/personal/sections/ClientsSection/components/ClientCard/ClientInfo.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Client } from '../../types/types';
import { TEXT_STYLES } from '@/theme/typography';
import { 
  formatSmartDate, 
  formatPhoneNumber, 
  formatPercent  
} from '@/utils/Formatters';
import { LOCALES } from '@/utils/constants';
import { Divider } from '@/components/ui/Layout';
import { useBusinessCurrency } from '@/hooks/useBusinessCurrency';

export interface ClientInfoProps {
  client: Client;
  clientInfo: any;
}

const InfoItem: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <label className={`block ${TEXT_STYLES.small} text-text`}>{label}</label>
    <div className={TEXT_STYLES.body}>{value || '—'}</div>
  </div>
);

export const ClientInfo: React.FC<ClientInfoProps> = ({
  clientInfo
}) => {
  const { t } = useTranslation(['clients', 'common']);
  const { formatPrice } = useBusinessCurrency();
  
  // Форматируем average_interval с одним знаком после запятой, но сохраняем как число
  const formattedFrequency = clientInfo.average_interval 
    ? Number(clientInfo.average_interval.toFixed(1))
    : 0;
    
return (
  <div className="space-y-6">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {/* Левая колонка */}
      <div className="space-y-4">
        <InfoItem label={t('info.name')} value={clientInfo.name} />
        <InfoItem 
          label={t('info.phone')} 
          value={clientInfo.phone ? formatPhoneNumber(clientInfo.phone) : '—'} 
        />
        <InfoItem label={t('info.email')} value={clientInfo.email || '—'} />
        <InfoItem label={t('info.lead_source')} value={t(`lead_sources.${clientInfo.leadsource}`)} />
        <InfoItem 
          label={t('info.discount')} 
          value={clientInfo.discount ? formatPercent(clientInfo.discount, 1, true, LOCALES.RU) : '0%'} 
        />
        <InfoItem 
          label={t('info.bonus')} 
          value={clientInfo.bonus ? `${clientInfo.bonus} ${t('common:points')}` : '0 ' + t('common:points')} 
        />
      </div>

      {/* Правая колонка */}
      <div className="space-y-4">
        <InfoItem 
          label={t('info.orders_count')} 
          value={clientInfo.orders_count.toString()} 
        />
        <InfoItem 
          label={t('info.average_check')} 
          value={formatPrice(clientInfo.average_check)} 
        />
        <InfoItem 
          label={t('info.total_amount')} 
          value={formatPrice(clientInfo.total_spent)}  
        />
        <InfoItem 
          label={t('info.frequency')} 
          value={t('info.days_count', { count: formattedFrequency })} 
        />
        <InfoItem 
          label={t('info.last_order')} 
          value={clientInfo.last_order_date ? formatSmartDate(clientInfo.last_order_date, false, t('common:locale')) : '—'} 
        />
        <InfoItem 
          label={t('info.created_at')} 
          value={clientInfo.created_at ? formatSmartDate(clientInfo.created_at, false, t('common:locale')) : '—'} 
        />
      </div>
    </div>

    <Divider />
    <AddressInfo lastOrderAddress={clientInfo.last_order_address} />
    
    {clientInfo.comment && (
      <>
        <Divider />
        <CommentInfo comment={clientInfo.comment} />
      </>
    )}
    
    <Divider />
    <SettingsInfo newsletterSubscription={clientInfo.newsletter_subscription} abandonedCarts={clientInfo.abandoned_carts} />
  </div>
);
};

const AddressInfo: React.FC<{ lastOrderAddress: string | null }> = ({ lastOrderAddress }) => {
const { t } = useTranslation(['clients', 'common']);

return (
  <div>
    <h4 className={`${TEXT_STYLES.small} text-text mb-2`}>
      {t('info.delivery_address')}
    </h4>
    <div className={TEXT_STYLES.body}>
      {lastOrderAddress || '—'}
    </div>
  </div>
);
};

const CommentInfo: React.FC<{ comment: string }> = ({ comment }) => {
const { t } = useTranslation(['clients']);

return (
  <div>
    <h4 className={`${TEXT_STYLES.small} text-text mb-2`}>
      {t('info.comment')}
    </h4>
    <div className={TEXT_STYLES.body}>{comment}</div>
  </div>
);
};

const SettingsInfo: React.FC<{ newsletterSubscription: boolean, abandonedCarts: boolean }> = ({ 
newsletterSubscription, 
abandonedCarts 
}) => {
const { t } = useTranslation(['clients']);

// Инвертируем значение newsletter_subscription для правильного отображения
const mailingEnabled = newsletterSubscription;

return (
  <div>
    <h4 className={`${TEXT_STYLES.small} text-text mb-2`}>
      {t('info.settings')}
    </h4>
    <div className="space-y-2">
      <div className="flex items-center">
        <span className={TEXT_STYLES.body}>{t('info.mailings')}:</span>
        <span className={`ml-2 ${TEXT_STYLES.small} ${mailingEnabled ? 'text-green-600' : 'text-red-500'}`}>
          {mailingEnabled ? t('info.enabled') : t('info.disabled')}
        </span>
      </div>
      <div className="flex items-center">
        <span className={TEXT_STYLES.body}>{t('info.abandoned_carts')}:</span>
        <span className={`ml-2 ${TEXT_STYLES.small} text-secondary`}>
          {abandonedCarts ? t('info.yes') : t('info.no')}
        </span>
      </div>
    </div>
  </div>
);
};

export default ClientInfo;